import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global keyboard shortcuts — admin/waiter productivity.
 *
 * Shortcuts:
 *   ?        — Open shortcut help modal
 *   Ctrl+/   — Alternatif help modal (Tur 315)
 *   g d      — Go dashboard (/)
 *   g t      — Go tables (/tables)
 *   g o      — Go orders (/orders)
 *   g k      — Go kitchen (/kitchen)
 *   g p      — Go POS (/pos)
 *   g c      — Go customers (/customers)
 *   g r      — Go reports (/reports)
 *   g s      — Go settings (/settings)
 *   g m      — Go menu management (/menu-management)
 *   g h      — Go HR (/hr)                 — Tur 315
 *   g u      — Go users (/users)           — Tur 315
 *   g x      — Go trash (/trash)           — Tur 315
 *   g v      — Go reservations (/reservations) — Tur 315 (rezerVations)
 *   Escape   — Close any open modal (handled per-component focus-trap)
 *
 * Ignores keys when focus is inside input/textarea/select/contentEditable.
 */

const ROUTE_MAP = {
  d: '/',
  t: '/tables',
  o: '/orders',
  k: '/kitchen',
  p: '/pos',
  c: '/customers',
  r: '/reports',
  s: '/settings',
  m: '/menu-management',
  h: '/hr',
  u: '/users',
  x: '/trash',
  v: '/reservations',
};

export default function useGlobalShortcuts() {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sequence, setSequence] = useState(null); // 'g' bekleme state

  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };

    const handler = (e) => {
      // Ctrl+K / Cmd+K — command palette toggle (input içinde de çalışır)
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        setPaletteOpen(p => !p);
        e.preventDefault();
        return;
      }

      // Ctrl+/ — alternatif help modal (Tur 315). Bazı terminal/RTL klavyelerde `?` zor erişilir.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key === '/') {
        setHelpOpen(h => !h);
        e.preventDefault();
        return;
      }

      // Modifier'lı kısayollar yok — sadece plain key (Ctrl/Cmd/Alt/Shift ignore)
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditable(e.target)) return;

      // Escape — help modal'ı kapat
      if (e.key === 'Escape' && helpOpen) {
        setHelpOpen(false);
        e.preventDefault();
        return;
      }

      // ? — help modal
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        setHelpOpen(h => !h);
        e.preventDefault();
        return;
      }

      // g-sequence başlat
      if (e.key === 'g' && !sequence) {
        setSequence('g');
        setTimeout(() => setSequence(null), 1500); // 1.5s timeout
        e.preventDefault();
        return;
      }

      // g + letter
      if (sequence === 'g' && ROUTE_MAP[e.key]) {
        const path = ROUTE_MAP[e.key];
        setSequence(null);
        navigate(path);
        e.preventDefault();
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, helpOpen, sequence]);

  return { helpOpen, closeHelp, paletteOpen, closePalette, sequence };
}

/** Tüm kısayolları tablosu — help modalda gösterilir */
export const SHORTCUT_LIST = [
  { keys: ['?'], key: 'shortcutHelp' },
  { keys: ['Ctrl', '/'], key: 'shortcutHelpAlt', fallbackKey: 'shortcutHelp' },
  { keys: ['Ctrl', 'K'], key: 'shortcutCommandPalette' },
  { keys: ['g', 'd'], key: 'shortcutDashboard', fallbackKey: 'shortcutHome' },
  { keys: ['g', 't'], key: 'shortcutTables' },
  { keys: ['g', 'o'], key: 'shortcutOrders' },
  { keys: ['g', 'k'], key: 'shortcutKitchen' },
  { keys: ['g', 'p'], key: 'shortcutPOS' },
  { keys: ['g', 'c'], key: 'shortcutCustomers' },
  { keys: ['g', 'r'], key: 'shortcutReports' },
  { keys: ['g', 's'], key: 'shortcutSettings' },
  { keys: ['g', 'm'], key: 'shortcutMenu' },
  { keys: ['g', 'h'], key: 'shortcutHr', fallbackKey: 'nav_hr' },
  { keys: ['g', 'u'], key: 'shortcutUsers', fallbackKey: 'nav_users' },
  { keys: ['g', 'v'], key: 'shortcutReservations', fallbackKey: 'nav_reservations' },
  { keys: ['g', 'x'], key: 'shortcutTrash', fallbackKey: 'trashTitle' },
  { keys: ['Esc'], key: 'shortcutClose' },
];
